import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { useSupportChatFabLayout, type SupportChatFabPanelMaxRem } from '@/stores/supportChatFabLayout.store';

export interface TrainerRightPanelHeaderProps {
  icon: LucideIcon;
  title: string;
  /** Sous-titre sous le titre (optionnel) */
  description?: string;
  onClose: () => void;
  closeDisabled?: boolean;
  titleId?: string;
  closeAriaLabel?: string;
  /** Actions additionnelles à droite du titre (ex. bouton modifier), avant la fermeture. */
  headerActions?: ReactNode;
  /**
   * Décale la bulle support pour qu’elle reste à gauche du panneau (`max-w-md` / `max-w-xl`).
   * Désactiver si le header n’est pas dans un overlay pleine hauteur à droite.
   */
  shiftSupportChatFab?: boolean;
  /** Aligné sur la classe du conteneur : `max-w-md` → 28rem, `max-w-xl` → 36rem. */
  supportFabPanelMaxWidth?: 'md' | 'xl';
}

/**
 * En-tête commun des panneaux latéraux droits du dashboard formateur
 * (fond crème Stratelya, titre navy, accent or sur l’icône).
 */
export function TrainerRightPanelHeader({
  icon: Icon,
  title,
  description,
  onClose,
  closeDisabled = false,
  titleId,
  closeAriaLabel = 'Fermer',
  shiftSupportChatFab = true,
  supportFabPanelMaxWidth = 'md',
  headerActions,
}: TrainerRightPanelHeaderProps) {
  const maxRem: SupportChatFabPanelMaxRem = supportFabPanelMaxWidth === 'xl' ? 36 : 28;

  useEffect(() => {
    if (!shiftSupportChatFab) return;
    const { pushPanel, popPanel } = useSupportChatFabLayout.getState();
    pushPanel(maxRem);
    return () => {
      popPanel();
    };
  }, [shiftSupportChatFab, maxRem]);

  return (
    <div className="relative shrink-0 border-b border-[#E5DDD2] bg-[#FDFBF7] px-5 pb-5 pt-5 sm:px-6 sm:pb-6 sm:pt-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#B38E5D] via-[#B38E5D]/50 to-transparent"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,#B38E5D_16%,white)] shadow-sm ring-1 ring-[#B38E5D]/35"
            aria-hidden
          >
            <Icon className="h-6 w-6 text-[#001B3D]" strokeWidth={2} />
          </span>
          <div className="min-w-0 pt-0.5">
            <h2
              id={titleId}
              className="text-lg font-semibold leading-tight tracking-tight text-[#001B3D]"
            >
              {title}
            </h2>
            {description ? (
              <p className="mt-1.5 line-clamp-4 text-sm leading-snug text-[#5C5C5C]">{description}</p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {headerActions}
          <button
            type="button"
            onClick={() => !closeDisabled && onClose()}
            disabled={closeDisabled}
            className="shrink-0 rounded-xl p-2 text-[#5C5C5C] transition hover:bg-[#F0EBE3] hover:text-[#001B3D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#001B3D] disabled:opacity-50"
            aria-label={closeAriaLabel}
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
